'use client';
import styled, { css } from 'styled-components';
import { generateContentIconStyle } from '@ui/icons';

export const CardWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 5px;
`;

export const Card = styled.div`
  border-radius: 32px;
  border: 2px solid
    ${props =>
      props.checked ? props.theme.colors.secondary.plum : 'transparent'};
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  overflow: hidden; /* prevent overflow on rounded corners */
  position: relative; /* allow absolute positioning of child elements */
  width: ${props =>
    props.orientation === 'column' ? '155px' : `${props.width}px`};
  max-height: ${props =>
    props.orientation === 'column'
      ? '155px'
      : props.showSelectionButton
      ? '190px'
      : '110px'};
  background: ${({ theme, unavailable }) =>
    unavailable
      ? theme.colors.background.disabled
      : theme.colors.background.lightest};
  opacity: ${({ $notSelectable }) => ($notSelectable ? 0.5 : 1)};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${props =>
    props.orientation === 'column'
      ? props.theme.spacing(2, 1)
      : props.theme.spacing(0, 0, 15.5)};

  @media ${props => props.theme.laptop} {
    width: ${props =>
      props.orientation === 'column'
        ? '155px'
        : props.showSelectionButton
        ? '276px'
        : `${props.width}px`};
  }
`;

export const CardContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${props => props.orientation};
  flex: 1;
  gap: 1px;
  padding: ${props =>
    props.orientation === 'column'
      ? '0'
      : props.showSelectionButton
      ? props.theme.spacing(3, 5)
      : props.theme.spacing(1, 2)};

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(1, 2)};
  }
`;

export const StyledImage = styled.div`
  display: flex;
  align-items: center;
  border-radius: 16px;
  overflow: hidden;
  object-position: left;
  object-fit: cover;
  height: ${props => (props.orientation === 'column' ? '85px' : '100px')};
  width: ${props => (props.orientation === 'column' ? '85px' : '100px')};
  filter: ${props => props.unavailable && 'grayscale(100%) brightness(93%)'};
  margin: ${props => props.theme.spacing(0, 2)};
  ${props =>
    !props.checked &&
    !props.unavailable &&
    props.orientation === 'column' &&
    css`
      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background-color: rgba(255, 255, 255, 0.5);
      }
    `};
  img {
    max-height: 100%;
    max-width: 82px;
  }
`;

export const Info = styled.div`
  max-width: 200px;
  text-align: left;
  padding-left: ${props =>
    props.orientation === 'column' ? '0px' : props.theme.spacing(2)};
`;

export const CardHeader = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(4)};
`;

export const Name = styled.h5`
  ${({ theme }) => theme.typography.heading5}
  text-transform: capitalize;
`;

export const Description = styled.p`
  display: ${props => (props.orientation === 'column' ? 'none' : 'inline')};
  ${({ theme }) => theme.typography.subheading}
  color: ${props => props.theme.colors.text.secondary};
  margin-top: ${props => props.theme.spacing(1)};
  text-transform: uppercase;
`;

export const MoreDetails = styled.button`
  ${({ theme }) => theme.typography.body}
  left: ${props => props.theme.spacing(4)};
  bottom: ${props => props.theme.spacing(8)};
  align-items: center;
  display: flex;
  border: none;
  padding: 0 3px;
  visibility: ${props =>
    props.unavailable || props.disabled ? 'hidden' : 'visible'};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.hero.plum};
  text-align: left;
  z-index: 1;

  svg {
    margin-left: ${props => props.theme.spacing(1)};
  }
  &:focus {
    border-radius: ${props => props.theme.spacing(2)};
    outline: 2px solid ${({ theme }) => theme.colors.neutral.darkest};
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
  border-radius: 200px;
  padding: ${props => props.theme.spacing(4)};
  background: ${props =>
    props.checked
      ? props.theme.colors.secondary.yellow
      : props.theme.colors.secondary.plum};
  background: ${props => props.unavailable && 'transparent'};
  min-width: 126px;
  max-width: 300px;
  height: 48px;
  align-items: center;
  ${props =>
    (props.icon || props.checked || props.unavailable) &&
    css`
      gap: ${props.theme.spacing(3)};
    `};

  ${props =>
    props.unavailable &&
    css`
      position: absolute;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      justify-content: space-between;
    `}
`;

export const ButtonWrapper = styled.div`
  position: static;
  width: 100%;
  padding: 2px;
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
  }

  &::before {
    ${props =>
      props.checked
        ? generateContentIconStyle('check')
        : props.unavailable
        ? generateContentIconStyle('close')
        : props.icon
        ? generateContentIconStyle(props.icon)
        : ''};
    width: ${props =>
      (props.icon || props.checked || props.unavailable) && '16px'};
    height: ${props =>
      (props.icon || props.checked || props.unavailable) && '16px'};
    background: ${props =>
      props.checked || props.unavailable
        ? props.theme.colors.text.primary
        : props.theme.colors.text.lightest};
  }

  &::after {
    content: '';
    border-radius: 32px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
  }
`;
export const LabelText = styled.span`
  ${({ theme }) => theme.typography.button}
  text-align: center;
  color: ${({ theme, checked, unavailable }) =>
    checked || unavailable
      ? theme.colors.text.primary
      : theme.colors.text.lightest};
`;

export const BadgeWrapper = styled.div`
  border-radius: 20px;
  position: absolute;
  top: -5px;
  right: 0;
  z-index: 1;
  p {
    ${props => props.theme.typography.subheading2};
    padding: ${props => props.theme.spacing(0.5, 3.5, 1)};
    max-height: 20px;
  }
`;
